import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import { toast } from 'react-toastify'

import * as interfaces from 'interfaces';
import { TrackerServiceInstance } from 'services';
import { useClientContext, useTranslationContext } from 'hooks';
import { VideoStateInterface } from '../index';

import Icons from 'shared/Icons';

import classes from './VideoControl.module.scss';

const SHAREABLE_LINKS = [
  {
    icon: 'twitter',
    text: 'Share',
    key: 'twitter'
  },
  {
    icon: 'facebook-square',
    text: 'Share',
    key: 'facebook'
  },
  {
    icon: 'envelope',
    text: 'Share',
    key: 'email'
  },
  {
    icon: 'copy',
    text: 'Copy Link',
    key: 'copy'
  }
];

interface VideoControlProps {
  selectedAnswer: interfaces.AnswerInterface | undefined;
  videoRef: React.RefObject<HTMLVideoElement>;
  videoState: VideoStateInterface;
  muted: boolean;
  setMuted: (m: boolean) => void;
  updateVideoState: (v: Partial<VideoStateInterface> | VideoStateInterface) => void;
  onFullScreen: (v: boolean) => void;
}

export default function VideoControl(props: VideoControlProps) {
  const { selectedAnswer, videoRef, videoState, muted, setMuted, updateVideoState, onFullScreen } = props;
  const { client } = useClientContext();
  const { t } = useTranslationContext();

  const [playedPart, setPlayedPart] = useState(0);
  const [showShareMenu, setShareMenu] = useState(false)

  const sliderRef = useRef<HTMLDivElement>(null);

  const handleVideoPauseEvent = useCallback(() => {
    updateVideoState({ paused: true });
  }, [updateVideoState]);

  const handleVideoSeekingEvent = useCallback(() => {
    updateVideoState({ seeking: true });
  }, [updateVideoState]);

  const handleVideoSeekedEvent = useCallback(
    event => {
      updateVideoState({ seeked: true, seeking: false });
    },
    [updateVideoState]
  );

  const handleVideoPlayEvent = useCallback(
    event => {
      if (!videoState.started) {
        TrackerServiceInstance?.trackEvent('setVideo', { event_category: 'Answers', answerId: selectedAnswer?.id });
        updateVideoState({ started: true, paused: false });
      } else {
        updateVideoState({ paused: false });
      }
    },
    [updateVideoState, videoState.started]
  );

  const handleTimeupdateEvent = useCallback(event => {
    if (videoRef.current) {
      const playingVideo = videoRef.current;
      const videoElementDuration = Number.isFinite(playingVideo.duration) ? playingVideo.duration : 100;
      const duration = selectedAnswer?.videoDuration || videoElementDuration;

      setPlayedPart(((playingVideo.currentTime || 0) / duration) * 100);

      const videoTimeExceeded = playingVideo.currentTime >= duration - 0.5
      if (videoTimeExceeded && !videoState.ended) {
        playingVideo.pause();
        handleVideoEndedEvent();
      }
    }
  }, [selectedAnswer]);

  const handleVideoEndedEvent = useCallback(() => {
      updateVideoState({
        isFeedbackShown: true,
        ended: true,
        started: false,
        paused: false,
        seeked: false
      });
    },
    [updateVideoState]
  );

  const handlePlayVideo = useCallback(
    (action: 'play' | 'pause') => {
      switch (action) {
        case 'play': {
          videoRef.current?.play();

          TrackerServiceInstance?.trackEvent('playVideo', { event_category: 'Answers', answerId: selectedAnswer?.id });

          break;
        }
        case 'pause': {
          videoRef.current?.pause();

          TrackerServiceInstance?.trackEvent('pauseVideo', { event_category: 'Answers', answerId: selectedAnswer?.id });

          break;
        }
      }
    },
    [selectedAnswer, videoRef]
  );

  const setVolume = useCallback(() => {
    TrackerServiceInstance?.trackEvent('setVideoVolume', { event_category: 'Answers', answerId: selectedAnswer?.id });
    setMuted(!muted);
    },
    [selectedAnswer, videoRef, muted]
  );

  const handleFullScreenToggle = useCallback(
    (action: 'expand' | 'compress') => {
      if (action == 'expand')
        TrackerServiceInstance?.trackEvent('setVideoFullScreen', {
          event_category: 'Answers',
          answerId: selectedAnswer?.id
        });

      switch (action) {
        case 'expand': {
          onFullScreen(true);

          break;
        }

        case 'compress': {
          onFullScreen(false);

          break;
        }
      }
    },
    [onFullScreen, selectedAnswer]
  );

  const onShareClick = useCallback(
    (channel: string) => {
      const host: string = window.location.hostname,
        clientPathName: string = window.location.pathname,
        shareURLBase: string = host === 'localhost' ? `http://localhost:3000${clientPathName}?aId=` : (
          host === 'staging.repd.us' ? `https://staging.repd.us${clientPathName}?aId=` : (
            `https://share.repd.us/og${clientPathName}/`
          )
        ),
        answerURL: string = `${shareURLBase}${selectedAnswer?.id}`,
        encodedAnswerURL: string = encodeURIComponent(answerURL),
        shareText: string = encodeURIComponent(selectedAnswer?.question?.text + '');

      if (channel === 'copy')
        TrackerServiceInstance?.trackEvent('copyLink', { event_category: 'Answers', answerId: selectedAnswer?.id });
      else
        TrackerServiceInstance?.trackEvent('shareVideo', { event_category: 'Answers', answerId: selectedAnswer?.id });

      switch (channel) {
        case 'twitter': {
          let shareURL = `https://twitter.com/intent/tweet?url=${encodedAnswerURL}&text=${shareText}`;

          if (shareURL.length > 280) {
            shareURL = `https://twitter.com/intent/tweet?url=${encodedAnswerURL}`;
          }

          window.open(shareURL, '_blank', 'noreferrer');

          break;
        }
        case 'facebook': {
          const shareURL = `https://www.facebook.com/sharer/sharer.php?u=${encodedAnswerURL}&t=${shareText}`;

          window.open(shareURL, '_blank', 'noreferrer');

          break;
        }
        case 'email': {
          const emailSubject = encodeURIComponent(`Candidate ${client.name}: ${selectedAnswer?.question?.text + ''}`);
          const emailBody =
            answerURL +
            '%0D%0A' +
            encodeURIComponent(
              `Check out ${client.firstName}'s answer to the question: ${selectedAnswer?.question?.text + ''}`
            );
          const shareURL = `mailto:?subject=${emailSubject}&body=${emailBody}`;

          window.open(shareURL, '_blank', 'noreferrer');

          break;
        }
        default: {
          const confirmText: string = `Please enable clipboard permissions, \nor copy this link directly: ${answerURL}`

          if (navigator?.permissions?.query)
            navigator.permissions.query({ name: 'clipboard-read', allowWithoutGesture: false } as any).then(permissionStatus => {
              // Will be 'granted', 'denied' or 'prompt':
              console.log(permissionStatus.state);

              // // Listen for changes to the permission state
              // permissionStatus.onchange = () => {
              //   console.log(permissionStatus.state);
              // };
            })

          if (window.navigator?.clipboard?.writeText)
            window.navigator.clipboard.writeText(answerURL).then(
              () => { },
              () => toast.error(confirmText, { autoClose: false, draggable: false })
            );
          else
            toast.error(confirmText, { autoClose: false, draggable: false })

          break;
        }
      }
    },
    [client, selectedAnswer]
  );

  const onSetCurrentTime = useCallback(
    event => {
      if (videoRef.current) {
        const playedVideo = videoRef.current;
        const videoElementDuration = Number.isFinite(playedVideo.duration) ? playedVideo.duration : 100;
        const duration = selectedAnswer?.videoDuration || videoElementDuration;

        const clickPositionOnTimeline = event.nativeEvent.offsetX,
          maxRatio = window.innerWidth < 768 ? 0.65 : 0.38,
          sliderWidth = sliderRef?.current?.clientWidth,
          newCurrentTime = (duration / 100) * Math.round(
            (clickPositionOnTimeline / (sliderWidth || (window.innerWidth * maxRatio))) * 100
          );
        playedVideo.currentTime = newCurrentTime;
        setPlayedPart(newCurrentTime / duration * 100);
      }
    },
    [videoRef, selectedAnswer]
  );

  useEffect(() => {
    if (!videoRef.current) return

    const videoElement: HTMLVideoElement = videoRef.current;

    if (videoElement) {
      videoElement.addEventListener('pause', handleVideoPauseEvent);
      videoElement.addEventListener('play', handleVideoPlayEvent);
      videoElement.addEventListener('ended', handleVideoEndedEvent);
      videoElement.addEventListener('seeking', handleVideoSeekingEvent);
      videoElement.addEventListener('seeked', handleVideoSeekedEvent);
      videoElement.addEventListener('timeupdate', handleTimeupdateEvent);
    }

    return () => {
      videoElement.removeEventListener('pause', handleVideoPauseEvent);
      videoElement.removeEventListener('play', handleVideoPlayEvent);
      videoElement.removeEventListener('ended', handleVideoEndedEvent);
      videoElement.removeEventListener('seeking', handleVideoSeekingEvent);
      videoElement.removeEventListener('seeked', handleVideoSeekedEvent);
      videoElement.removeEventListener('timeupdate', handleTimeupdateEvent);
    };
  }, [
    handleVideoPauseEvent,
    handleVideoPlayEvent,
    handleVideoEndedEvent,
    handleVideoSeekingEvent,
    handleVideoSeekedEvent,
    handleTimeupdateEvent,
    videoRef
  ]);

  const videoIsPausedAndStarted: boolean = (
    videoState.started &&
    videoState.paused &&
    !videoState.ended &&
    (videoRef?.current?.currentTime || 0) > 0.5 &&
    (videoRef?.current?.currentTime || 0) < (videoRef?.current?.duration || 1)
  )

  return (
    <div
      className={cn(
        classes.VideoControl,
        videoState.isFullScreen && classes.fullScreen
      )}
    >
      <div className={classes.playSection}>
        {videoRef.current?.paused ? (
          <Icons iconType="play" iconClass={classes.controlIcon} handleClick={() => handlePlayVideo('play')} />
        ) : (
          <Icons iconType="pause" iconClass={classes.controlIcon} handleClick={() => handlePlayVideo('pause')} />
        )}
      </div>

      <div className={classes.timeline} ref={sliderRef}>
        <div className={classes.totalTime} onClick={onSetCurrentTime}>
          <div className={classes.currentTime} style={{ width: `${playedPart}%` }}></div>
          <div className={classes.currentTimeCircle} style={{ left: `${playedPart}%` }}></div>
        </div>
      </div>

      <div className={classes.remainingControl}>
        <Icons
          iconType={muted ? "volume-xmark" : "volume-high"}
          iconClass={classes.controlIcon}
          handleClick={() => setVolume()}
        />

        {videoState.isFullScreen ? (
          <Icons
            iconType="compress"
            iconClass={classes.controlIcon}
            handleClick={() => handleFullScreenToggle('compress')}
          />
        ) : (
          <Icons
            iconType="expand"
            iconClass={classes.controlIcon}
            handleClick={() => handleFullScreenToggle('expand')}
          />
        )}

        <div className={cn(classes.shareLinksBlock, showShareMenu ? classes.active : '')} onClick={() => setShareMenu(!showShareMenu)}>
          <Icons iconType="share" iconClass={classes.controlIcon} />

          <div className={classes.shareOptions} onMouseOut={() => setShareMenu(false)} onBlur={() => setShareMenu(false)}>
            {SHAREABLE_LINKS.map(({ key, icon, text }) => (
              <div key={key} className={classes.shareLink} onClick={() => onShareClick(key)} onMouseOver={() => setShareMenu(true)}>
                <Icons iconType={icon} iconClass={classes.optionIcon} />
                {t(text)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
